import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '~/components/Button';
import { showToast } from '~/components/Toast';
import { FEATURE_FLAGS } from '~/modules/experiments/features';
import { useFeatureFlags } from '~/modules/experiments/hooks/useFeatureFlags';
import { CTABlock } from '~/modules/settings/components/CTABlock';
import { SettingsPageHeader } from '~/modules/settings/components/SettingsPageHeader';
import { SETTINGS_ROUTES } from '~/modules/settings/config/routes';
import type { UserFormSchema } from '~/modules/settings/modules/user/components/UserForm';
import {
  mapFormDataToApi,
  UserForm,
} from '~/modules/settings/modules/user/components/UserForm';
import type { UserDetail } from '~settings/api/lib/schemas';
import { useCreateUser } from '~settings/api/mutations/userCreate';
import { getErrorMessage } from '~settings/modules/user/helpers';

const FORM_ID = 'createUserForm';

export function CreateUserPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();

  const [isInternalUpdate] = useFeatureFlags([
    FEATURE_FLAGS.SettingsInternalUserUpdates,
  ]);

  const { mutate: createUser, isPending } = useCreateUser({
    onSuccess: (data: UserDetail) => {
      navigate(SETTINGS_ROUTES.USER_DETAIL(data.id));
    },
    onError: async (error) => {
      const message = await getErrorMessage(error, true);

      showToast({
        title: t(message.title),
        description: t(message.description),
      });
    },
  });

  const onSubmit = (data: UserFormSchema) => {
    const apiData = mapFormDataToApi(data, isInternalUpdate);

    createUser(apiData);
  };

  return (
    <article className="relative flex min-h-full flex-col">
      <SettingsPageHeader
        breadcrumbItems={[
          { to: SETTINGS_ROUTES.ROOT, label: 'title' },
          { to: SETTINGS_ROUTES.USER_LIST, label: 'users' },
          { to: '', label: 'user.createUserTitle' },
        ]}
        title="user.createUserTitle"
      />
      <div className="grow overflow-y-auto pb-xl">
        <UserForm onSubmit={onSubmit} id={FORM_ID} />
      </div>
      <CTABlock>
        <Button asChild variant="secondary" size="large">
          <Link to={SETTINGS_ROUTES.USER_LIST}>{t('buttons.cancel')}</Link>
        </Button>
        <Button
          variant="primary"
          type="submit"
          size="large"
          form={FORM_ID}
          disabled={isPending}
        >
          {t('user.addUserButton')}
        </Button>
      </CTABlock>
    </article>
  );
}

CreateUserPage.displayName = 'CreateUserPage';
